import React from 'react';
import { GenericSearchSelect, SelectableItem } from './GenericSearchSelect';
import { useAppSelector } from '../../../../../contexts/hooks';
import { useConfigReady } from '../../../../../hooks/useConfigReady';
import { selectEstudiosDeMiSede, selectEstudiosVisibles } from '../../../../../contexts/selectors/config.selectors';

// Interfaz específica para estudios
export interface StudyItem extends SelectableItem {
  id: number | string;
  idlr: number | null;
  idAr: number;
  code: string;
  name: string;
  group: string;
  area?: string;
  isActive?: boolean;
}

// Props del componente de estudios
interface StudiesSearchSelectProps {
  onStudySelected: (study: StudyItem | null) => void;
  selectedStudies?: StudyItem[];
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  showGroups?: boolean;
  minSearchLength?: number;
  keepLabel?: boolean; // Nueva prop para mantener visible el elemento seleccionado
  /**
   * Recorta además a los estudios del perfil de la sesión (ModuleStudiesByProfile). Se usa en
   * órdenes y en validación masiva; cotizaciones no lo usa, ahí se ofrece cualquier estudio.
   */
  soloEstudiosDelPerfil?: boolean;
}

export const StudiesSearchSelect: React.FC<StudiesSearchSelectProps> = ({
  onStudySelected,
  selectedStudies = [],
  placeholder = 'Buscar estudios...',
  label = 'Buscar estudios',
  disabled = false,
  className = '',
  showGroups = true,
  minSearchLength = 1, // Cambiar a 1 para ser igual que cotizaciones
  keepLabel = false, // Por defecto false para mantener comportamiento existente
  soloEstudiosDelPerfil = false,
}) => {
  const { pending: configPending } = useConfigReady();

  // Estudios de las areas de la sede en la que se abrio sesion (y del perfil, si se pide), solo los activos.
  const estudiosDeMiSede = useAppSelector(soloEstudiosDelPerfil ? selectEstudiosVisibles : selectEstudiosDeMiSede);
  const studiesData = React.useMemo(() => estudiosDeMiSede.filter((study) => study.Es_Estado === true), [estudiosDeMiSede]);

  // Crear el dataSource para el componente genérico con lazy loading
  const dataSource = {
    load: async (options?: { skip?: number; take?: number; searchText?: string }): Promise<StudyItem[]> => {
      const { skip = 0, take = 200, searchText = '' } = options || {};
      
      // Filtrar estudios por texto de búsqueda si se proporciona
      let filteredStudies = studiesData;
      if (searchText && searchText.length >= minSearchLength) {
        const search = searchText.toLowerCase();
        filteredStudies = studiesData.filter(study =>
          study.Es_Nombre.toLowerCase().includes(search) ||
          study.Es_Codigo.toLowerCase().includes(search) ||
          study.SysA_AreasAnalisis?.Aa_Nombre?.toLowerCase().includes(search)
        );
      } else if (searchText && searchText.length > 0 && searchText.length < minSearchLength) {
        // Si hay texto pero no cumple la longitud mínima, no mostrar nada
        filteredStudies = [];
      }
      
      // Simular paginación
      const paginatedStudies = filteredStudies.slice(skip, skip + take);
      
      return paginatedStudies.map(study => ({
        id: study.Es_Id,
        idlr: study.Es_Lr_Id || null,
        code: study.Es_Codigo,
        idAr: study.Es_Aa_Id,
        name: study.Es_Nombre,
        group: study.SysA_AreasAnalisis?.Aa_Nombre || 'Sin Área',
        area: study.SysA_AreasAnalisis?.Aa_Nombre,
        isActive: study.Es_Estado || false,
        // Agregar datos originales por si se necesitan
        originalData: study
      }));
    },
    
    getTotalCount: async (searchText?: string): Promise<number> => {
      if (!searchText || searchText.length < minSearchLength) {
        return studiesData.length;
      }
      
      const search = searchText.toLowerCase();
      const filteredStudies = studiesData.filter(study =>
        study.Es_Nombre.toLowerCase().includes(search) ||
        study.Es_Codigo.toLowerCase().includes(search) ||
        study.SysA_AreasAnalisis?.Aa_Nombre?.toLowerCase().includes(search)
      );
      
      return filteredStudies.length;
    }
  };

  // Manejar selección de estudio
  const handleItemSelected = (item: SelectableItem | null) => {    
    if (!item) {
      // Si es null, el usuario limpió la selección
      onStudySelected(null);
      return;
    }
    
    const studyItem: StudyItem = {
      id: item.id,
      idlr: item.originalData.Es_Lr_Id || null,
      idAr: item.originalData.Es_Aa_Id,
      code: item.code || '',
      name: item.name,
      group: item.group || '',
      area: (item as any).area,
      isActive: (item as any).isActive,
    };
    onStudySelected(studyItem);
  };

  // Los estudios llegan con GetAllConfig, después de autenticar. Mientras no
  // estén, buscar sobre una lista vacía haría creer que no hay estudios.
  const loadingStudies = configPending && studiesData.length === 0;

  return (
    <GenericSearchSelect
      dataSource={dataSource}
      onItemSelected={handleItemSelected}
      selectedItems={selectedStudies}
      placeholder={loadingStudies ? 'Cargando estudios...' : placeholder}
      label={label}
      disabled={disabled || loadingStudies}
      className={className}
      searchFields={['name', 'code', 'group']}
      groupField="group"
      displayFields={{
        title: 'name',
        subtitle: 'code'
      }}
      showGroups={showGroups}
      minSearchLength={minSearchLength}
      keepLabel={keepLabel}
    />
  );
};
